// DOM UI overlay layer. Plain HTML/CSS on top of the canvas — no framework.
//
// Three overlays, switched by game state:
//   MENU / LOADING  → splash: title, controls legend, "click to begin"
//   PAUSED          → "Paused" overlay with resume instructions
//   PLAYING (+PAUSED) → HUD in the corner with altitude + speed readouts
//
// The UI subscribes to `stateChange` on the Game instance and updates
// visibility automatically. The main loop calls `updateHUD()` each frame.

import { GameState } from './game.js';

const CSS = `
	.bj-overlay {
		position: fixed; inset: 0;
		display: flex; flex-direction: column; align-items: center; justify-content: center;
		background: rgba(8, 12, 20, 0.72);
		color: #f0f0f0;
		font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
		z-index: 10;
		cursor: pointer;
		text-align: center;
		backdrop-filter: blur(3px);
		-webkit-backdrop-filter: blur(3px);
		user-select: none;
	}
	.bj-overlay h1 { font-size: 2.8em; margin: 0 0 0.3em; letter-spacing: 0.01em; font-weight: 600; }
	.bj-overlay h2 { font-size: 2.2em; margin: 0 0 0.4em; font-weight: 500; }
	.bj-overlay p  { font-size: 1.1em; margin: 0 0 1.2em; opacity: 0.85; }
	.bj-overlay[hidden] { display: none; }
	.bj-controls {
		background: rgba(0,0,0,0.35);
		padding: 0.9em 1.4em;
		border-radius: 8px;
		font-size: 0.95em;
		line-height: 1.8;
		text-align: left;
		font-family: monospace;
		color: #d8d8d8;
	}
	.bj-key { color: #ffcc33; display: inline-block; min-width: 5.5em; }

	.bj-progress {
		width: 320px;
		height: 8px;
		background: rgba(255, 255, 255, 0.15);
		border-radius: 4px;
		margin: 0.4em 0 0.8em;
		overflow: hidden;
	}
	.bj-progress-bar {
		height: 100%;
		width: 0%;
		background: #ffcc33;
		transition: width 0.18s ease-out;
	}
	.bj-progress-text {
		font-family: monospace;
		font-size: 0.95em;
		opacity: 0.75;
	}

	#bj-hud {
		position: fixed; top: 14px; left: 14px;
		color: #f0f0f0;
		font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
		font-size: 13px;
		background: rgba(0,0,0,0.4);
		padding: 7px 11px;
		border-radius: 5px;
		z-index: 5;
		pointer-events: none;
		line-height: 1.5;
		letter-spacing: 0.04em;
		white-space: pre;
	}
	#bj-hud[hidden] { display: none; }
	.bj-hud-label { opacity: 0.55; }

	/* Full-screen black wash for level transitions (the hell respawn). */
	#bj-fade {
		position: fixed; inset: 0;
		background: #000;
		opacity: 0;
		z-index: 20;
		pointer-events: none;
		transition: opacity 0.6s linear;
	}
`;

export class UI {
	constructor(game, canvas) {
		this.game = game;
		this.canvas = canvas;

		this._injectStyles();
		this._buildDOM();
		this._bindClicks();

		game.on('stateChange', (next) => this._sync(next));
		this._sync(game.state);
	}

	// Called every frame from the main loop while playing/paused.
	updateHUD(position, velocity) {
		this.elAltitude.textContent = position.y.toFixed(0).padStart(5);
		this.elSpeed.textContent = velocity.length().toFixed(1).padStart(6);
	}

	// ─── DOM construction ───────────────────────────────────────

	_injectStyles() {
		const style = document.createElement('style');
		style.textContent = CSS;
		document.head.appendChild(style);
	}

	_buildDOM() {
		// Loading overlay: visible during AssetLoader run. Same visual style
		// as the splash so the transition feels seamless.
		this.loading = document.createElement('div');
		this.loading.className = 'bj-overlay';
		this.loading.style.cursor = 'default';
		this.loading.innerHTML = `
			<h1>Bungee Jumping Simulator</h1>
			<div class="bj-progress"><div class="bj-progress-bar" id="bj-progress-bar"></div></div>
			<p class="bj-progress-text"><span id="bj-progress-text">0%</span></p>
		`;

		this.splash = document.createElement('div');
		this.splash.className = 'bj-overlay';
		this.splash.innerHTML = `
			<h1>Bungee Jumping Simulator</h1>
			<p>Click to begin</p>
			<div class="bj-controls">
				<div><span class="bj-key">Space / ↑</span> jump</div>
				<div><span class="bj-key">Drag</span> look around</div>
				<div><span class="bj-key">Esc / P</span> pause</div>
				<div><span class="bj-key">R</span> restart</div>
			</div>
		`;

		this.pause = document.createElement('div');
		this.pause.className = 'bj-overlay';
		this.pause.innerHTML = `
			<h2>Paused</h2>
			<p>Press <strong>Esc</strong> or <strong>P</strong> (or click) to resume</p>
		`;

		this.hud = document.createElement('div');
		this.hud.id = 'bj-hud';
		this.hud.innerHTML =
			`<div><span class="bj-hud-label">ALT  </span><span id="bj-hud-altitude">    0</span></div>` +
			`<div><span class="bj-hud-label">SPD  </span><span id="bj-hud-speed">   0.0</span></div>`;

		this.fade = document.createElement('div');
		this.fade.id = 'bj-fade';

		document.body.append(this.loading, this.splash, this.pause, this.hud, this.fade);

		this.elProgressBar = this.loading.querySelector('#bj-progress-bar');
		this.elProgressText = this.loading.querySelector('#bj-progress-text');
		this.elAltitude = this.hud.querySelector('#bj-hud-altitude');
		this.elSpeed = this.hud.querySelector('#bj-hud-speed');
	}

	// Called by AssetLoader's progress callback.
	updateLoading(loaded, total) {
		const pct = total === 0 ? 100 : Math.round((loaded / total) * 100);
		this.elProgressBar.style.width = pct + '%';
		this.elProgressText.textContent = pct + '%';
	}

	// ─── Transition fades (hell respawn) ────────────────────────
	// Resolve after the transition so callers can `await` the wash before
	// swapping levels behind the black, then fade back in.
	fadeOut(ms = 600) {
		this.fade.style.transition = `opacity ${ms}ms ease-in`;
		this.fade.style.opacity = '1';
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	fadeIn(ms = 800) {
		this.fade.style.transition = `opacity ${ms}ms ease-out`;
		this.fade.style.opacity = '0';
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	_bindClicks() {
		// Splash/pause overlays just transition state — pointer lock is now
		// engaged on canvas mousedown by InputManager, not on these clicks.
		this.splash.addEventListener('click', () => this.game.start());
		this.pause.addEventListener('click', () => this.game.togglePause());
	}

	// ─── State sync ─────────────────────────────────────────────

	_sync(state) {
		this.loading.hidden = state !== GameState.LOADING;
		this.splash.hidden  = state !== GameState.MENU;
		this.pause.hidden   = state !== GameState.PAUSED;
		// HUD stays visible during pause so the player sees their last readout.
		this.hud.hidden     = state !== GameState.PLAYING && state !== GameState.PAUSED;
	}
}
